import { z } from 'zod'

export const featureContributionSchema = z.object({
  feature: z.string(),
  contribution: z.number(),
  value: z.number(),
})

/**
 * One scoring of a prospect by the LeadScorer — the full model input/output.
 *
 * Keyed by `id` (random UUID, same pattern as all other tables). `external_id`
 * and `salesforce_event_id` are regular fields; add a GSI on `external_id` when
 * per-prospect history queries are needed. Raw email/phone/name are intentionally
 * absent; the model's signal survives in `top_features` / `missing_fields`.
 * See docs/adr/0005.
 */
export const leadScoreRecordSchema = z.object({
  // Key
  id: z.string().uuid(),
  // References
  external_id: z.string().nullish(),
  analytics_id: z.string(),
  salesforce_event_id: z.string().nullish(),
  salesforce_lead_id: z.string().nullish(),
  scored_at: z.string().datetime(),
  model_version: z.string(),
  score_type: z.enum(['initial', 'wealthfeed']).default('initial'),
  // Model inputs
  created_date: z.string().nullish(),
  lead_source: z.string().nullish(),
  investable_assets_range: z.string().nullish(),
  annual_income: z.string().nullish(),
  age: z.string().nullish(),
  marital_status: z.string().nullish(),
  utm_source: z.string().nullish(),
  utm_medium: z.string().nullish(),
  utm_campaign: z.string().nullish(),
  utm_content: z.string().nullish(),
  zip: z.string().nullish(),
  assets: z.string().nullish(),
  email_opt_out: z.string().nullish(),
  // Model outputs
  lead_score: z.number(),
  p_good: z.number(),
  p_bad: z.number(),
  p_signed: z.number(),
  net_score: z.number(),
  score_tier: z.string(),
  low_confidence: z.boolean(),
  top_features: z.array(featureContributionSchema),
  missing_fields: z.array(z.string()),
  // Present only when score_type === 'wealthfeed' — raw enrichment data
  // passed through to sync-to-salesforce so it can write WealthFeed_*__c fields.
  wf_match: z.boolean().nullish(),
  wf_net_worth: z.number().nullable().nullish(),
  wf_home_value: z.number().nullable().nullish(),
  wf_job_title: z.string().nullable().nullish(),
  wf_phone_dnc: z.boolean().nullable().nullish(),
  wf_event_type: z.string().nullable().nullish(),
  wf_event_date: z.string().nullable().nullish(),
})

export type LeadScoreRecord = z.infer<typeof leadScoreRecordSchema>
export type CreateLeadScoreRecord = Omit<LeadScoreRecord, 'id'>
